import { EventEmitter } from "events";

import WeakFunctionPool from "../functionResolvers/weakFunctionPool.functionResolver";
import DefaultModelResolver from "../modelResolvers/default.modelResolver";
import NoCompressionResolver from "../compressResolvers/noCompression.compressionResolver";
import UuidIdResolver from "../idResolvers/uuid.idResolver";
import { FunctionResolver } from "../functionResolvers";
import { ModelResolver } from "../modelResolvers";
import { CompressResolver } from "../compressResolvers";
import { ModifiedWebSocket } from "../utils/websocketModifier.util";
import { WsMessageEvent } from "../utils/wsModule";
import typeAssert, { DataObject } from "../utils/typeAssert.util";
import RuntimeError from "../utils/error.utils";
import badRequestUtil from "../utils/badRequest.util";
import { IdResolver } from "../idResolvers";
import {
	NormalizedTransfer,
	TransferFlags,
	normalizeTransferFlags,
} from "../transfer";

function toWsPayload(data: unknown): string | ArrayBuffer {
	if (typeof data === "string") {
		return data;
	}
	if (data instanceof ArrayBuffer) {
		return data;
	}
	if (ArrayBuffer.isView(data)) {
		const view = new Uint8Array(
			data.buffer,
			data.byteOffset,
			data.byteLength,
		);
		const copy = new Uint8Array(view.byteLength);
		copy.set(view);
		return copy.buffer;
	}
	return String(data);
}

export default class UniversalRPC extends EventEmitter {
	public session: ModifiedWebSocket;

	public readonly sessionId: string;

	public modelResolver: ModelResolver;

	private readonly functionResolver: FunctionResolver;

	private readonly compressResolver: CompressResolver;

	private idResolver: IdResolver;

	public theirsModel: any = null;

	private _closeRequestSides: Function[];

	constructor(
		session: ModifiedWebSocket,
		options: {
			transfer?: TransferFlags | NormalizedTransfer;
		},
	) {
		super();
		const transfer = normalizeTransferFlags(
			options.transfer as TransferFlags,
		);

		this._closeRequestSides = [];
		this.session = session;
		this.sessionId = session.sessionId;
		this.theirsModel = null;

		this.modelResolver = new DefaultModelResolver({
			session: this.session,
		});
		this.idResolver = new UuidIdResolver({
			session: this.session,
		});
		this.functionResolver = new WeakFunctionPool({
			session: this.session,
			sendMessage: this.sendFunctionResolver.bind(this),
			deSerializeObject: this.modelResolver.deserialize.bind(
				this.modelResolver,
			),
			serializeObject: this.modelResolver.serialize.bind(
				this.modelResolver,
			),
			uuid: this.idResolver.gen.bind(this.idResolver),
		});
		this.compressResolver = new NoCompressionResolver({
			session: this.session,
			transfer,
		});

		this.session.addEventListener(
			"message",
			async (event: WsMessageEvent) => {
				try {
					const payload = (await this.compressResolver.decompress(
						event,
					)) as DataObject;
					await this.onMessage(payload);
				} catch (error) {
					console.error("[prpcow] decode failed", error);
					this.emit("error", error as Error);
				}
			},
		);
		this.session.addEventListener("close", this.close.bind(this));
	}

	private async send(data: any) {
		if (this.session.readyState !== (this.session.OPEN ?? 1)) {
			throw new Error("Session is not Opened. Cannot send data");
		}
		return this.session.send(
			toWsPayload(await this.compressResolver.compress(data)),
		);
	}

	async onMessage(requestData: DataObject) {
		if (this.session.readyState !== (this.session.OPEN ?? 1)) {
			return;
		}
		try {
			typeAssert(
				requestData,
				{
					upgradeModel: async () => {
						this.theirsModel = Object.seal(
							await this.modelResolver.deserialize(
								requestData.data,
								this.functionResolver.setTheirs.bind(
									this.functionResolver,
								),
							),
						);
						this.emit("theirsModelChange", this.theirsModel);
					},
					functionResolver: () => {
						this.functionResolver.onMessage(requestData.data);
					},
					error: () => {
						this.emit(
							"error",
							new RuntimeError(
								requestData.error.message,
								requestData.error.status,
								requestData.error.name,
							),
						);
					},
					closeRequest: async () => {
						await Promise.all(
							this.listeners("closeRequest").map((item) =>
								item(),
							),
						);
						try {
							await this.send({
								type: "closeRequestConfirm",
							});
						} catch {
							// session may already be gone
						}
					},
					closeRequestConfirm: async () => {
						if (this._closeRequestSides.length) {
							await Promise.all(
								this._closeRequestSides.map((resolve) =>
									resolve(),
								),
							);
							this.session.close();
						} else {
							throw new Error("Close request not created");
						}
					},
					ready: async () => {},
				},
				() => {
					throw new Error("Unexpected Type");
				},
			);
		} catch (e: any) {
			try {
				await this.send(badRequestUtil(e));
			} catch {
				// ignore
			}
			this.emit("error", e);
			this.session.close();
			await this.close();
		}
	}

	private async sendFunctionResolver(data: any) {
		await this.send({
			type: "functionResolver",
			data,
		});
	}

	public async close() {
		this.functionResolver.close();
		this.idResolver.close();
		this.emit("close");
	}

	public async closeRequest() {
		const closeLength = this._closeRequestSides.length;
		const requestClosePromise = new Promise((resolve: Function) => {
			this._closeRequestSides.push(() => resolve());
		});

		if (!closeLength) {
			await this.send({
				type: "closeRequest",
			});
		}
		return requestClosePromise;
	}

	addEventListener(
		method: "theirsModelChange",
		cb: (event: any) => void,
	): void;
	addEventListener(method: "error", cb: (error: Error) => void): void;
	addEventListener(method: "close", cb: () => void): void;

	addEventListener(event: string, listener: (...args: any[]) => void) {
		return this.addListener(event, listener);
	}
}
